import { SdkContext } from '../internal'
import type {
  Channel,
  ChannelListParams,
  ChannelMentionable,
  ChannelMessage,
  ChannelMessageInput,
  ChannelPage,
  ChannelPagination,
  ChannelReaction,
  ChannelThreadPage,
  ChannelThreadState,
  ChannelUnread
} from '@/types/channels'

const EMPTY_PAGE: ChannelPagination = { hasMoreBefore: false, hasMoreAfter: false, oldestId: null, newestId: null }

const itemsOf = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return (Array.isArray(r?.items) && r?.items) || (Array.isArray(r?.data) && r?.data) || []
}

const paginationOf = (result: unknown): ChannelPagination =>
  ({ ...EMPTY_PAGE, ...((result as { pagination?: ChannelPagination } | null)?.pagination ?? {}) })

/**
 * F-138 Canal. Every route lives under `/channels` with the community from
 * X-CommunityId; all need login. Paths are written by hand (F-03).
 */
export class ChannelsResource {
  constructor(private ctx: SdkContext) {}

  /** One row per channel space the person can open, for the menu. */
  /** Mutes the channel for the viewer (no bell, no e-mail for mentions and replies; unread counts stay). */
  async mute(spaceId: string): Promise<{ spaceId: string; muted: boolean }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/channels/{spaceId}/mute', hasBody: true }, { spaceId, body: {} })
  }

  async unmute(spaceId: string): Promise<{ spaceId: string; muted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/channels/{spaceId}/mute' }, { spaceId })
  }

  async listUnread(): Promise<ChannelUnread[]> {
    return itemsOf<ChannelUnread>(await this.ctx.call({ httpMethod: 'GET', path: '/channels/unread' }))
  }

  async get(spaceId: string): Promise<Channel> {
    return this.ctx.call({ httpMethod: 'GET', path: '/channels/{spaceId}' }, { spaceId })
  }

  /** Oldest to newest, 50 at most. */
  async listMessages(spaceId: string, params: ChannelListParams = {}): Promise<ChannelPage> {
    const { before, after, around, ids, limit } = params
    const result = await this.ctx.raw(
      { httpMethod: 'GET', path: '/channels/{spaceId}/messages' },
      { spaceId, query: { before, after, around, ids: ids && ids.length ? ids.join(',') : undefined, limit } }
    )
    return { items: itemsOf<ChannelMessage>(result), pagination: paginationOf(result) }
  }

  async post(spaceId: string, body: ChannelMessageInput): Promise<ChannelMessage> {
    return this.ctx.call({ httpMethod: 'POST', path: '/channels/{spaceId}/messages', hasBody: true }, { spaceId, body })
  }

  async markRead(spaceId: string, messageId?: string | null): Promise<ChannelUnread> {
    return this.ctx.call({ httpMethod: 'POST', path: '/channels/{spaceId}/read', hasBody: true }, { spaceId, body: messageId ? { messageId } : {} })
  }

  /** At least 3 letters; never e-mail nor role. */
  async mentionable(spaceId: string, q: string, limit = 8): Promise<ChannelMentionable[]> {
    return itemsOf<ChannelMentionable>(
      await this.ctx.call({ httpMethod: 'GET', path: '/channels/{spaceId}/mentionable' }, { spaceId, query: { q, limit } })
    )
  }

  async edit(id: string, body: string): Promise<ChannelMessage> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/channels/messages/{id}', hasBody: true }, { id, body: { body } })
  }

  async remove(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/channels/messages/{id}' }, { id })
  }

  async listReplies(id: string, params: Omit<ChannelListParams, 'ids'> = {}): Promise<ChannelThreadPage> {
    const { before, after, around, limit } = params
    const result = await this.ctx.raw<unknown>(
      { httpMethod: 'GET', path: '/channels/messages/{id}/replies' },
      { id, query: { before, after, around, limit } }
    )
    /* The thread's `data` is an object, so the client keeps `{ data, pagination }` as it came. */
    const r = result as { data?: { root?: ChannelMessage; replies?: ChannelMessage[] }; root?: ChannelMessage; replies?: ChannelMessage[] } | null
    const data = r?.data && !Array.isArray(r.data) ? r.data : r
    return { root: data?.root ?? null, replies: data?.replies ?? [], pagination: paginationOf(result) }
  }

  async reply(id: string, body: ChannelMessageInput): Promise<ChannelMessage> {
    return this.ctx.call({ httpMethod: 'POST', path: '/channels/messages/{id}/replies', hasBody: true }, { id, body })
  }

  async markThreadRead(id: string): Promise<ChannelThreadState> {
    return this.ctx.call({ httpMethod: 'POST', path: '/channels/messages/{id}/read', hasBody: true }, { id })
  }

  async follow(id: string): Promise<ChannelThreadState> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/channels/messages/{id}/follow' }, { id })
  }

  async unfollow(id: string): Promise<ChannelThreadState> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/channels/messages/{id}/follow' }, { id })
  }

  async react(id: string, emoji: string): Promise<{ messageId: string; reactions: ChannelReaction[] }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/channels/messages/{id}/reactions/{emoji}' }, { id, emoji })
  }

  async unreact(id: string, emoji: string): Promise<{ messageId: string; reactions: ChannelReaction[] }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/channels/messages/{id}/reactions/{emoji}' }, { id, emoji })
  }
}
